import {useRef} from 'react'
import type {PickerOption} from '@shared/components/base/inputs/Picker'

/**
 * Every name a picker has seen, kept across pages and searches.
 *
 * A `Picker` remembers the rows picked through it, which covers a selection
 * made in this session. It cannot cover one that arrives from somewhere else —
 * a URL parameter, a saved template, a stored widget — because nothing ever
 * handed it those rows, and the page the server returns need not contain them.
 *
 * A ref rather than state: remembering a name must never itself cause a render.
 */
export const useSeenOptionNames = (rows: {id: string; name: string}[]): Record<string, string> => {
  const seen = useRef<Record<string, string>>({})
  rows.forEach((row) => {
    seen.current[row.id] = row.name
  })
  return seen.current
}

/**
 * The listed options plus one entry per selected id that is not among them.
 *
 * Without it a selection the current page does not contain draws no chip at
 * all, so a list is narrowed by something the trigger does not show. The
 * fallback is the bare id, which is what the selects this replaces showed for a
 * selection they could not name either.
 */
export const withUnlistedSelection = (
  options: PickerOption[],
  selected: string[],
  names: Record<string, string>,
): PickerOption[] => {
  const listed = new Set(options.map((option) => option.id))
  return [
    ...options,
    ...selected.filter((id) => !listed.has(id)).map((id) => ({id, name: names[id] ?? id})),
  ]
}
